import React, { useState, useEffect, useRef, useCallback } from 'react';
import { motion, AnimatePresence, useScroll, useTransform, useMotionValue, useSpring, useInView } from 'framer-motion';
import { Github, ExternalLink, ChevronDown, Star, Eye, Code, Zap, Smartphone, Globe, Database, Cpu, Palette, ShoppingCart } from 'lucide-react';

// Projects data array
const PROJECTS = [
  {
    title: "E-Commerce Platform",
    description: "A full-stack e-commerce platform with real-time inventory management, secure payment processing, and advanced analytics dashboard.",
    type: "Full Stack",
    typeColor: "#00f5ff",
    tags: ["React", "Node.js", "MongoDB", "Stripe", "Socket.io"],
    github: "https://github.com/yourusername/ecommerce-platform",
    live: "https://your-ecommerce-demo.com",
    liveColor: "#00f5ff",
    icon: <ShoppingCart size={32} />,
    iconBg: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
    revealColor: "#667eea",
    blobColors: ["#667eea", "#764ba2", "#00f5ff"],
    featured: true
  },
  {
    title: "Task Management App",
    description: "A collaborative task management application with real-time updates, drag-and-drop functionality, and team collaboration features.",
    type: "Frontend",
    typeColor: "#ff00f5",
    tags: ["React", "Redux", "Firebase", "Material-UI", "Framer Motion"],
    github: "https://github.com/yourusername/task-manager",
    live: "https://your-task-manager.com",
    liveColor: "#ff00f5",
    icon: <Code size={32} />,
    iconBg: "linear-gradient(135deg, #f093fb 0%, #f5576c 100%)",
    revealColor: "#f093fb",
    blobColors: ["#f093fb", "#f5576c", "#ff00f5"]
  },
  {
    title: "AI-Powered Analytics Dashboard",
    description: "An intelligent analytics dashboard with machine learning insights, predictive analytics, and interactive data visualizations.",
    type: "Data Science",
    typeColor: "#00ff88",
    tags: ["Python", "TensorFlow", "React", "D3.js", "FastAPI"],
    github: "https://github.com/yourusername/ai-dashboard",
    live: "https://your-ai-dashboard.com",
    liveColor: "#00ff88",
    icon: <Database size={32} />,
    iconBg: "linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)",
    revealColor: "#4facfe",
    blobColors: ["#4facfe", "#00f2fe", "#00ff88"],
    featured: true
  },
  {
    title: "Mobile Banking App",
    description: "A secure mobile banking application with biometric authentication, real-time transactions, and comprehensive financial management tools.",
    type: "Mobile",
    typeColor: "#ffd700",
    tags: ["React Native", "TypeScript", "Firebase", "Stripe", "Expo"],
    github: "https://github.com/yourusername/mobile-banking",
    live: "https://your-banking-app.com",
    liveColor: "#ffd700",
    icon: <Smartphone size={32} />,
    iconBg: "linear-gradient(135deg, #fa709a 0%, #fee140 100%)",
    revealColor: "#fa709a",
    blobColors: ["#fa709a", "#fee140", "#ffd700"]
  },
  {
    title: "Real Estate Platform",
    description: "A comprehensive real estate platform with property listings, virtual tours, mortgage calculator, and agent communication system.",
    type: "Full Stack",
    typeColor: "#00f5ff",
    tags: ["Next.js", "PostgreSQL", "Prisma", "Tailwind", "Mapbox"],
    github: "https://github.com/yourusername/real-estate",
    live: "https://your-real-estate.com",
    liveColor: "#00f5ff",
    icon: <Globe size={32} />,
    iconBg: "linear-gradient(135deg, #a8edea 0%, #fed6e3 100%)",
    revealColor: "#a8edea",
    blobColors: ["#a8edea", "#fed6e3", "#00f5ff"]
  },
  {
    title: "Social Media Dashboard",
    description: "A unified social media management dashboard with content scheduling, analytics tracking, and multi-platform posting capabilities.",
    type: "Full Stack",
    typeColor: "#ff00f5",
    tags: ["Vue.js", "Express.js", "MySQL", "Redis", "Bull Queue"],
    github: "https://github.com/yourusername/social-dashboard",
    live: "https://your-social-dashboard.com",
    liveColor: "#ff00f5",
    icon: <Palette size={32} />,
    iconBg: "linear-gradient(135deg, #ffecd2 0%, #fcb69f 100%)",
    revealColor: "#ffecd2",
    blobColors: ["#ffecd2", "#fcb69f", "#ff00f5"],
    featured: true
  },
  {
    title: "IoT Monitoring System",
    description: "An IoT monitoring system for smart buildings with real-time sensor data, automated alerts, and energy consumption optimization.",
    type: "IoT",
    typeColor: "#00ff88",
    tags: ["React", "Node.js", "InfluxDB", "MQTT", "Docker"],
    github: "https://github.com/yourusername/iot-monitoring",
    live: "https://your-iot-system.com",
    liveColor: "#00ff88",
    icon: <Cpu size={32} />,
    iconBg: "linear-gradient(135deg, #84fab0 0%, #8fd3f4 100%)",
    revealColor: "#84fab0",
    blobColors: ["#84fab0", "#8fd3f4", "#00ff88"]
  },
  {
    title: "Portfolio Website",
    description: "A modern, responsive portfolio website with smooth animations, dark mode support, and optimized performance.",
    type: "Frontend",
    typeColor: "#ffd700",
    tags: ["React", "Framer Motion", "Tailwind", "Vite", "Netlify"],
    github: "https://github.com/yourusername/portfolio",
    live: "https://your-portfolio.com",
    liveColor: "#ffd700",
    icon: <Globe size={32} />,
    iconBg: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
    revealColor: "#667eea",
    blobColors: ["#667eea", "#764ba2", "#ffd700"]
  }
];

// Morphing blob background component
const MorphingBlob = ({ colors, isActive }) => {
  return (
    <motion.div
      className="morphing-blob"
      animate={{
        borderRadius: [
          "60% 40% 30% 70%/60% 30% 70% 40%",
          "30% 60% 70% 40%/50% 60% 30% 60%",
          "70% 30% 60% 40%/30% 70% 40% 60%",
          "40% 70% 30% 60%/70% 40% 60% 30%",
          "60% 40% 30% 70%/60% 30% 70% 40%"
        ],
        background: isActive ? [
          `radial-gradient(circle at 20% 50%, ${colors[0]}40, transparent 50%)`,
          `radial-gradient(circle at 80% 50%, ${colors[1]}40, transparent 50%)`,
          `radial-gradient(circle at 50% 20%, ${colors[2]}40, transparent 50%)`,
          `radial-gradient(circle at 50% 80%, ${colors[0]}40, transparent 50%)`,
          `radial-gradient(circle at 20% 50%, ${colors[1]}40, transparent 50%)`
        ] : `radial-gradient(circle at 50% 50%, ${colors[0]}20, transparent 50%)`,
        scale: isActive ? [1, 1.2, 1.1, 1.3, 1] : [1, 1.05, 1],
        rotate: [0, 120, 240, 360],
        opacity: isActive ? [0.6, 0.8, 0.7, 0.9, 0.6] : [0.3, 0.4, 0.3]
      }}
      transition={{
        duration: 8,
        repeat: Infinity,
        ease: "easeInOut"
      }}
    />
  );
};

// Holographic shine effect
const HolographicShine = ({ isActive }) => {
  return (
    <motion.div
      className="holographic-shine"
      animate={{
        background: [
          "linear-gradient(45deg, transparent 30%, rgba(255, 255, 255, 0.1) 50%, transparent 70%)",
          "linear-gradient(135deg, transparent 30%, rgba(0, 245, 255, 0.2) 50%, transparent 70%)",
          "linear-gradient(225deg, transparent 30%, rgba(255, 0, 245, 0.2) 50%, transparent 70%)",
          "linear-gradient(315deg, transparent 30%, rgba(255, 255, 255, 0.1) 50%, transparent 70%)",
          "linear-gradient(45deg, transparent 30%, rgba(255, 255, 255, 0.1) 50%, transparent 70%)"
        ],
        backgroundPosition: isActive ? ["0% 0%", "100% 100%", "0% 100%", "100% 0%", "0% 0%"] : ["0% 0%", "25% 25%", "0% 0%"],
        opacity: isActive ? [0.8, 1, 0.9, 1, 0.8] : [0.4, 0.6, 0.4]
      }}
      transition={{
        duration: 4,
        repeat: Infinity,
        ease: "easeInOut"
      }}
    />
  );
};

// Enhanced Project Card without drag functionality
const EnhancedCard = ({ project, index, isVisible, cardRef, onCardInView }) => {
  const mouseX = useMotionValue(0);
  const mouseY = useMotionValue(0);
  const [isHovered, setIsHovered] = useState(false);
  const [isInViewport, setIsInViewport] = useState(false);
  
  const cardInView = useInView(cardRef, { 
    threshold: 0.6,
    margin: "-80px 0px"
  });

  useEffect(() => {
    if (cardInView && !isInViewport) {
      setIsInViewport(true);
      onCardInView(index);
    }
  }, [cardInView, index, onCardInView, isInViewport]);

  const springConfig = { damping: 25, stiffness: 400, mass: 0.5 };
  const rotateX = useSpring(useTransform(mouseY, [-10, 10], [-12, 12]), springConfig);
  const rotateY = useSpring(useTransform(mouseX, [-10, 10], [12, -12]), springConfig);

  const handleMouseMove = ({ clientX, clientY, currentTarget }) => {
    const { left, top, width, height } = currentTarget.getBoundingClientRect();
    const centerX = left + width / 2;
    const centerY = top + height / 2;
    const rotateXValue = (clientY - centerY) / 10;
    const rotateYValue = (clientX - centerX) / 10;
    
    mouseX.set(rotateYValue);
    mouseY.set(rotateXValue);
  };

  const handleMouseLeave = () => {
    setIsHovered(false);
    mouseX.set(0);
    mouseY.set(0);
  };

  const cardVariants = {
    hidden: { 
      opacity: 0, 
      scale: 0.7,
      rotateX: -30,
      filter: "blur(20px)"
    },
    visible: { 
      opacity: 1, 
      scale: 1,
      rotateX: 0,
      filter: "blur(0px)",
      transition: { 
        type: "spring",
        damping: 15,
        stiffness: 100,
        mass: 0.8,
        staggerChildren: 0.2
      }
    }
  };

  const contentVariants = {
    hidden: { opacity: 0, y: 40, scale: 0.8 },
    visible: { 
      opacity: 1, 
      y: 0, 
      scale: 1,
      transition: { 
        type: "spring",
        damping: 12,
        stiffness: 200
      }
    }
  };

  return (
    <AnimatePresence>
      {isVisible && (
        <motion.div
          ref={cardRef}
          variants={cardVariants}
          initial="hidden"
          animate="visible"
          exit="hidden"
          className="quantum-card"
          style={{
            rotateX,
            rotateY,
            transformStyle: "preserve-3d"
          }}
          onMouseMove={handleMouseMove}
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={handleMouseLeave}
          whileHover={{ 
            scale: 1.02,
            y: -8,
            transition: { duration: 0.3 }
          }}
        >
          {/* Morphing blob background */}
          <MorphingBlob colors={project.blobColors} isActive={isHovered} />
          
          {/* Holographic shine effect */}
          <HolographicShine isActive={isHovered} />
          
          {/* Enhanced magical particles */}
          <div className="enhanced-particles">
            {[...Array(12)].map((_, i) => (
              <motion.div
                key={i}
                className="quantum-particle"
                animate={{
                  x: [
                    Math.random() * 400 - 200,
                    Math.random() * 400 - 200,
                    Math.random() * 400 - 200
                  ],
                  y: [
                    Math.random() * 400 - 200,
                    Math.random() * 400 - 200,
                    Math.random() * 400 - 200
                  ],
                  scale: [0, 1, 0.5, 1, 0],
                  opacity: [0, 1, 0.7, 1, 0],
                  rotate: [0, 180, 360]
                }}
                transition={{
                  duration: 5 + Math.random() * 3,
                  repeat: Infinity,
                  delay: Math.random() * 4,
                  ease: "easeInOut"
                }}
                style={{
                  background: `radial-gradient(circle, ${project.typeColor}80, ${project.liveColor}40)`
                }}
              />
            ))}
          </div>

          {/* Liquid loading effect */}
          <motion.div
            className="liquid-loader"
            initial={{ scaleX: 0 }}
            animate={{ scaleX: 1 }}
            transition={{
              duration: 1.5,
              ease: [0.25, 0.46, 0.45, 0.94],
              delay: index * 0.2
            }}
            style={{
              background: `linear-gradient(90deg, ${project.typeColor}, ${project.liveColor})`
            }}
          />

          {/* Enhanced entry number */}
          <motion.div 
            className="quantum-number"
            variants={contentVariants}
            whileHover={{ 
              scale: 1.3,
              rotate: [0, 360],
              boxShadow: `0 0 40px ${project.typeColor}80`
            }}
            animate={{
              boxShadow: [
                `0 0 20px ${project.typeColor}40`,
                `0 0 35px ${project.liveColor}40`,
                `0 0 20px ${project.typeColor}40`
              ]
            }}
            transition={{ 
              boxShadow: { duration: 3, repeat: Infinity },
              rotate: { duration: 0.8 },
              scale: { duration: 0.3 }
            }}
            style={{
              background: `conic-gradient(from 0deg, ${project.typeColor}, ${project.liveColor}, ${project.typeColor})`
            }}
          >
            <span className="neon-text">{String(index + 1).padStart(2, '0')}</span>
          </motion.div>

          {/* Enhanced project icon */}
          <motion.div 
            className="quantum-icon"
            variants={contentVariants}
            whileHover={{ 
              scale: 1.2,
              rotate: [0, -10, 10, 0],
              filter: "drop-shadow(0 0 20px rgba(255, 255, 255, 0.5))"
            }}
            animate={{
              y: [0, -8, 0],
              filter: [
                "drop-shadow(0 0 10px rgba(255, 255, 255, 0.2))",
                "drop-shadow(0 0 20px rgba(255, 255, 255, 0.4))",
                "drop-shadow(0 0 10px rgba(255, 255, 255, 0.2))"
              ]
            }}
            transition={{ 
              y: { duration: 3, repeat: Infinity },
              filter: { duration: 2, repeat: Infinity }
            }}
            style={{ background: project.iconBg }}
          >
            <motion.div
              animate={{ 
                rotate: isHovered ? [0, 360] : 0,
                scale: isHovered ? [1, 1.1, 1] : 1
              }}
              transition={{ 
                rotate: { duration: 1.5, ease: "easeInOut" },
                scale: { duration: 0.5 }
              }}
            >
              {project.icon}
            </motion.div>
          </motion.div>

          <motion.div className="quantum-content" variants={contentVariants}>
            <div className="project-meta">
              <motion.span 
                className="quantum-type neon-text"
                whileHover={{ 
                  scale: 1.1,
                  textShadow: `0 0 25px ${project.typeColor}`
                }}
                style={{ color: project.typeColor }}
                animate={{
                  textShadow: [
                    `0 0 10px ${project.typeColor}60`,
                    `0 0 20px ${project.typeColor}80`,
                    `0 0 15px ${project.typeColor}60`
                  ]
                }}
                transition={{ duration: 2.5, repeat: Infinity }}
              >
                {project.type}
              </motion.span>
              {project.featured && (
                <motion.div 
                  className="quantum-badge"
                  whileHover={{ 
                    scale: 1.15,
                    y: -3
                  }}
                  animate={{ 
                    boxShadow: [
                      "0 0 15px rgba(255, 215, 0, 0.4)",
                      "0 0 30px rgba(255, 215, 0, 0.6)",
                      "0 0 15px rgba(255, 215, 0, 0.4)"
                    ]
                  }}
                  transition={{ duration: 3, repeat: Infinity }}
                >
                  <motion.div
                    animate={{ rotate: 360 }}
                    transition={{ duration: 5, repeat: Infinity, ease: "linear" }}
                  >
                    <Star size={14} fill="currentColor" />
                  </motion.div>
                  <span className="neon-text">Featured</span>
                </motion.div>
              )}
            </div>

            <motion.h3 
              className="quantum-title"
              whileHover={{ 
                x: 15,
                color: project.typeColor,
                textShadow: `0 0 20px ${project.typeColor}60`
              }}
              variants={contentVariants}
            >
              {project.title}
            </motion.h3>

            <motion.p 
              className="quantum-description"
              variants={contentVariants}
              whileHover={{ 
                color: "#ffffff",
                textShadow: "0 0 15px rgba(255, 255, 255, 0.3)"
              }}
            >
              {project.description}
            </motion.p>

            <motion.div 
              className="quantum-stack"
              variants={contentVariants}
            >
              {project.tags.map((tag, tagIndex) => (
                <motion.span
                  key={tag}
                  className="quantum-tag"
                  whileHover={{ 
                    scale: 1.15, 
                    y: -8,
                    boxShadow: `0 10px 30px ${project.typeColor}40`,
                    textShadow: `0 0 10px ${project.typeColor}80`
                  }}
                  initial={{ opacity: 0, y: 30, scale: 0.8 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  transition={{ 
                    delay: tagIndex * 0.15,
                    type: "spring",
                    damping: 10,
                    stiffness: 200
                  }}
                >
                  {tag}
                </motion.span>
              ))}
            </motion.div>

            <motion.div 
              className="quantum-actions"
              variants={contentVariants}
            >
              {project.github && (
                <motion.a
                  href={project.github}
                  className="quantum-btn github-btn"
                  whileHover={{ 
                    scale: 1.08, 
                    y: -5,
                    boxShadow: "0 20px 40px rgba(255, 255, 255, 0.3)"
                  }}
                  whileTap={{ scale: 0.95 }}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <Github size={20} />
                  <span>View Code</span>
                  <div className="liquid-effect" />
                </motion.a>
              )}
              {project.live && (
                <motion.a
                  href={project.live}
                  className="quantum-btn live-btn"
                  whileHover={{ 
                    scale: 1.08, 
                    y: -5,
                    boxShadow: `0 20px 40px ${project.liveColor}50`
                  }}
                  whileTap={{ scale: 0.95 }}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ '--btn-color': project.liveColor }}
                >
                  <ExternalLink size={20} />
                  <span>Live Demo</span>
                  <div className="liquid-effect" />
                </motion.a>
              )}
            </motion.div>
          </motion.div>

          {/* Enhanced reveal overlay */}
          <motion.div
            className="quantum-overlay"
            initial={{ 
              clipPath: "polygon(0 0, 100% 0, 100% 100%, 0 100%)" 
            }}
            animate={{ 
              clipPath: "polygon(0 0, 100% 0, 100% 0%, 0 0%)" 
            }}
            transition={{ 
              duration: 1.5,
              ease: [0.25, 0.46, 0.45, 0.94],
              delay: index * 0.15
            }}
            style={{ 
              background: `linear-gradient(135deg, ${project.revealColor}cc, ${project.typeColor}99, transparent)` 
            }}
          />
        </motion.div>
      )}
    </AnimatePresence>
  );
};

const ProjectsProgressive = () => {
  const [revealedCount, setRevealedCount] = useState(1);
  const [visibleProjects, setVisibleProjects] = useState(new Set([0]));
  const [achievements, setAchievements] = useState([]);
  const containerRef = useRef(null);
  const cardRefs = useRef([]);

  const { scrollYProgress } = useScroll({ 
    target: containerRef, 
    offset: ["start start", "end end"] 
  });

  const headerY = useTransform(scrollYProgress, [0, 0.1], ['0%', '-20%']);

  const handleCardInView = useCallback((index) => {
    setVisibleProjects(prev => new Set([...prev, index]));
    
    // Check for achievements
    if (index === PROJECTS.length - 1) {
      setAchievements(prev => [...prev, {
        id: 'explorer',
        title: 'Project Explorer',
        description: 'Viewed all projects!',
        icon: '🏆'
      }]);
    }
    
    if (index === revealedCount - 1 && revealedCount < PROJECTS.length) {
      setTimeout(() => {
        setRevealedCount(prev => prev + 1);
      }, 600);
    }
  }, [revealedCount]);

  const revealNext = () => {
    setRevealedCount(prev => Math.min(PROJECTS.length, prev + 1));
  };

  const showAll = () => {
    setRevealedCount(PROJECTS.length);
    setVisibleProjects(new Set(PROJECTS.map((_, i) => i)));
  };

  const titleVariants = {
    hidden: { opacity: 0, y: 100, scale: 0.8 },
    visible: { 
      opacity: 1, 
      y: 0,
      scale: 1,
      transition: { 
        type: "spring",
        damping: 12,
        stiffness: 100,
        mass: 0.8
      }
    }
  };

  return (
    <div id="projects" className="quantum-projects-section" ref={containerRef}>
      {/* Achievement notifications */}
      <AnimatePresence>
        {achievements.map((achievement, index) => (
          <motion.div
            key={achievement.id}
            className="achievement-popup"
            initial={{ opacity: 0, y: -50, scale: 0.8 }}
            animate={{ opacity: 1, y: 20, scale: 1 }}
            exit={{ opacity: 0, y: -50, scale: 0.8 }}
            transition={{ delay: index * 0.2 }}
            style={{ right: `${20 + index * 320}px` }}
          >
            <div className="achievement-icon">{achievement.icon}</div>
            <div className="achievement-info">
              <h4>{achievement.title}</h4>
              <p>{achievement.description}</p>
            </div>
          </motion.div>
        ))}
      </AnimatePresence>

      <motion.div 
        className="quantum-header"
        style={{ y: headerY }}
        variants={titleVariants}
        initial="hidden"
        animate="visible"
      >
        <motion.h2 className="quantum-title">
          <motion.span
            className="quantum-text"
            animate={{ 
              backgroundPosition: ["0% 50%", "100% 50%", "0% 50%"],
              textShadow: [
                "0 0 50px rgba(0, 245, 255, 0.4)",
                "0 0 70px rgba(255, 0, 245, 0.4)",
                "0 0 60px rgba(0, 255, 136, 0.4)",
                "0 0 80px rgba(255, 215, 0, 0.4)",
                "0 0 50px rgba(0, 245, 255, 0.4)"
              ]
            }}
            transition={{ 
              backgroundPosition: { duration: 5, repeat: Infinity, ease: "linear" },
              textShadow: { duration: 6, repeat: Infinity, ease: "easeInOut" }
            }}
          >
            Selected Works
          </motion.span>
          <motion.span 
            className="quantum-subtitle"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1, duration: 1 }}
          >
            Experience the future of interactive portfolios
          </motion.span>
        </motion.h2>
      </motion.div>

      <div className="quantum-container">
        <div className="quantum-grid">
          {PROJECTS.map((project, index) => (
            <EnhancedCard
              key={`${project.title}-${index}`}
              project={project}
              index={index}
              isVisible={index < revealedCount}
              cardRef={el => (cardRefs.current[index] = el)}
              onCardInView={handleCardInView}
            />
          ))}
        </div>

        {/* Enhanced quantum controls */}
        <AnimatePresence>
          {revealedCount < PROJECTS.length && (
            <motion.div 
              className="quantum-controls"
              initial={{ opacity: 0, y: 80, scale: 0.8 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: -80, scale: 0.8 }}
              transition={{ 
                type: "spring",
                damping: 12,
                stiffness: 200
              }}
            >
              <motion.button
                className="quantum-control-btn primary"
                onClick={revealNext}
                whileHover={{ 
                  scale: 1.1,
                  y: -5,
                  boxShadow: "0 25px 60px rgba(0, 245, 255, 0.5)"
                }}
                whileTap={{ scale: 0.95 }}
                animate={{
                  boxShadow: [
                    "0 15px 40px rgba(0, 245, 255, 0.3)",
                    "0 20px 50px rgba(255, 0, 245, 0.3)",
                    "0 15px 40px rgba(0, 245, 255, 0.3)"
                  ]
                }}
                transition={{ duration: 2.5, repeat: Infinity }}
              >
                <motion.div
                  animate={{ rotate: 360 }}
                  transition={{ duration: 4, repeat: Infinity, ease: "linear" }}
                >
                  <Zap size={22} />
                </motion.div>
                <span>Reveal Next Project</span>
                <motion.div
                  animate={{ y: [0, 8, 0] }}
                  transition={{ duration: 1.8, repeat: Infinity }}
                >
                  <ChevronDown size={22} />
                </motion.div>
                <div className="liquid-effect" />
              </motion.button>
              
              <motion.button
                className="quantum-control-btn secondary"
                onClick={showAll}
                whileHover={{ 
                  scale: 1.1,
                  y: -5,
                  boxShadow: "0 25px 60px rgba(255, 0, 245, 0.5)"
                }}
                whileTap={{ scale: 0.95 }}
                animate={{
                  boxShadow: [
                    "0 12px 35px rgba(255, 0, 245, 0.3)",
                    "0 16px 45px rgba(0, 245, 255, 0.3)",
                    "0 12px 35px rgba(255, 0, 245, 0.3)"
                  ]
                }}
                transition={{ duration: 3, repeat: Infinity }}
              >
                <motion.div
                  animate={{ 
                    scale: [1, 1.2, 1],
                    rotate: [0, 360]
                  }}
                  transition={{ 
                    scale: { duration: 2, repeat: Infinity },
                    rotate: { duration: 6, repeat: Infinity, ease: "linear" }
                  }}
                >
                  <Eye size={18} />
                </motion.div>
                <span>Show All Projects</span>
                <div className="liquid-effect" />
              </motion.button>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};

export default ProjectsProgressive;
